import {
  MetricBucket,
  MetricCatalogEntry,
  MetricGroup,
  MetricUnit,
  MetricsResponse,
} from "../useMetrics";

/**
 * Row/section/grid helpers shared by the on-screen metric table, the export
 * menu and the print report. This mirrors `MetricTable.tsx`'s internal
 * `buildGrid` exactly so Copy / CSV / Print all serialize the same numbers.
 */
export interface MetricRowDef {
  key: string;
  label: string;
  unit: MetricUnit;
  definition?: string;
  /** Catalog metric this row renders; absent for keyed-breakdown rows. */
  entry?: MetricCatalogEntry;
  referralType?: string;
  assistanceType?: string;
}

export interface MetricSectionDef {
  title: string;
  rows: MetricRowDef[];
}

export const bucketValue = (
  bucket: MetricBucket | undefined | null,
  row: MetricRowDef,
): number => {
  if (!bucket) return 0;
  const v = row.referralType
    ? (bucket.referralTypes || {})[row.referralType]
    : row.assistanceType
      ? (bucket.assistanceRequested || {})[row.assistanceType]
      : bucket[row.entry ? row.entry.id : row.key];
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
};

/** Catalog -> sections, with the keyed breakdowns appended last. */
export const buildMetricSections = (
  data: MetricsResponse | null,
): MetricSectionDef[] => {
  if (!data) return [];
  const bySection: MetricSectionDef[] = [];
  const index = new Map<string, MetricSectionDef>();
  (data.catalog || []).forEach((entry) => {
    if (entry.id === "referralTypes" || entry.id === "assistanceRequested") {
      return;
    }
    const title = entry.section || "Metrics";
    let section = index.get(title);
    if (!section) {
      section = { title, rows: [] };
      index.set(title, section);
      bySection.push(section);
    }
    section.rows.push({
      key: entry.id,
      label: entry.label,
      unit: entry.unit,
      definition: entry.definition,
      entry,
    });
  });
  const typeKeys = Object.keys(data.total?.referralTypes || {});
  if (typeKeys.length > 0) {
    bySection.push({
      title: "Referral Types",
      rows: typeKeys.map((type) => ({
        key: `referralType:${type}`,
        label: type,
        unit: "count" as MetricUnit,
        referralType: type,
      })),
    });
  }
  const assistanceKeys = Object.keys(data.total?.assistanceRequested || {});
  if (assistanceKeys.length > 0) {
    bySection.push({
      title: "Assistance Requested",
      rows: assistanceKeys.map((category) => ({
        key: `assistance:${category}`,
        label: category,
        unit: "count" as MetricUnit,
        assistanceType: category,
      })),
    });
  }
  return bySection;
};

const asText = (value: number, unit: MetricUnit) =>
  unit === "hours" ? value.toFixed(1) : String(value);

/**
 * Metric x group grid: header row, one bare-title row per section, one row per
 * metric with a cell per group plus a Total column.
 */
export const buildMetricGrid = (data: MetricsResponse | null): string[][] => {
  if (!data) return [];
  const groups: MetricGroup[] = data.groups || [];
  const header = ["Metric", ...groups.map((g) => g.label), "Total"];
  const grid: string[][] = [header];
  buildMetricSections(data).forEach((section) => {
    grid.push([section.title]);
    section.rows.forEach((row) => {
      grid.push([
        row.label,
        ...groups.map((g) => asText(bucketValue(g.metrics, row), row.unit)),
        asText(bucketValue(data.total, row), row.unit),
      ]);
    });
  });
  return grid;
};

export default buildMetricGrid;
